import React, { useMemo, forwardRef } from 'react';

import classnames from 'classnames';

import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import { usePaywallContext } from 'components/Paywall';

import { TP, DATE_FORMATS } from 'constants/index';

import { getFormattedDateRange } from 'utils/date';
import { getCityCountry } from 'utils/globals';
import usePageContext from 'utils/hooks/usePageContext';
import { getEntityLatestUpcomingPerformance } from 'utils/productions';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES } from 'operabase-router/constants';

import { SECTION_KEYS } from '../constants';
import SectionHeader from '../SectionHeader';
import { scrollToSection } from '../utils';

import classes from './DateAndVenue.module.scss';

const RenderVenue = ({ performance }) => {
  const { venue } = performance || {};
  const venueName = venue?.name;
  const cityCountry = getCityCountry(performance);
  return (
    <>
      {cityCountry} {venueName && `(${venueName})`}
    </>
  );
};

const SingleDate = ({ production, selectedPerformance, isProdStubPage, isStickyHeader, styles }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const performance = selectedPerformance || production?.performances?.[0];
  const { rangeStart, weekdayStart, fromISO } = useMemo(
    () =>
      getFormattedDateRange({
        from: performance?.startDate,
        to: performance?.startDate,
        asParts: true,
        dateFormat: DATE_FORMATS.DAY_SHORT_MONTH_YEAR_2,
      }),
    [performance?.startDate],
  );
  const stageName = performance?.stage?.name;

  return (
    <div className={classnames(classes.contentMiddle, { [styles?.singleDateRoot]: !!styles?.singleDateRoot })}>
      <div className={classes.dateRoot}>
        {isProdStubPage && (
          <Typography variant="p" size={16}>
            {t(`${TP}.NEXT_UPCOMING`)}
          </Typography>
        )}
        <Typography variant="p" weight="bold" size={16}>
          <time dateTime={fromISO}>{rangeStart}</time>
        </Typography>
        <Typography variant="p" size={16} color="secondary" className={classes.time}>
          {weekdayStart}
          {performance?.startTime && (
            <>
              <SpriteIcon icon="schedule" size={12} />
              {performance?.startTime}
            </>
          )}
        </Typography>
      </div>
      {!isStickyHeader && (
        <>
          <Typography size={14} color="secondary" align="center" truncate>
            <SpriteIcon icon="location" size={12} className={classes.locationIcon} />
            <RenderVenue performance={performance} />
          </Typography>
          {stageName && (
            <Typography size={12} color="secondary" align="center" italic truncate>
              {stageName}
            </Typography>
          )}
        </>
      )}
    </div>
  );
};

const MultipleDates = ({ production, isStickyHeader, t }) => {
  const { setIsPaywallExpanded } = usePaywallContext();
  const isArchived = production?.isArchived;
  const { rangeStart, rangeEnd, separator, fromISO, toISO } = useMemo(
    () => getFormattedDateRange({ from: production?.minDate, to: production?.maxDate, asParts: true }),
    [production?.minDate, production?.maxDate],
  );
  const performanceCount = production?.performances?.length;
  const venues = production?.venues;
  return (
    <div className={classes.contentMiddle}>
      <Typography variant="p" weight="bold" size={16}>
        <time dateTime={fromISO}>{rangeStart}</time>
        {separator}
        {rangeEnd && <time dateTime={toISO}>{rangeEnd}</time>}
      </Typography>
      {!isStickyHeader && (
        <>
          <Typography size={14} color="secondary" textTransform="lowercase" align="center">
            ({performanceCount}{' '}
            {performanceCount === 1 ? t(`${TP}.FN_ADD_PRODUCTION_PERFORMACE`) : t(`${TP}.FN_PERFORMANCES`)})
          </Typography>
          {venues?.length && (
            <div className={classes.venueRow}>
              <Typography
                size={12}
                color="secondary"
                align="center"
                className={classes.venueName}
                italic
                blur={isArchived}
                {...(isArchived && {
                  onClick: e => {
                    e.stopPropagation();
                    e.preventDefault();
                    setIsPaywallExpanded(true);
                  },
                })}
              >
                <RenderVenue performance={production?.performances?.[0]} />{' '}
              </Typography>
              {venues?.length > 1 && (
                <Typography
                  onClick={() => scrollToSection(SECTION_KEYS.VENUES)}
                  className={classes.moreVenueLink}
                  size={12}
                  align="center"
                  italic
                  underline
                >
                  {t(`${TP}.FN_PLUS_X_MORE_NO_BRACKET`, { number: venues?.length - 1 })}
                </Typography>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const DateAndVenue = forwardRef(
  (
    {
      production,
      selectedPerformanceId,
      setSelectedPerformanceId,
      isStickyHeader = false,
      styles = { singleDateRoot: '' },
    },
    ref,
  ) => {
    const { t } = useTranslation('NS_ENTITY_STUB_PAGE');
    const performances = production?.performances;
    const hasSinglePerformance = performances?.length === 1;
    const { entityType } = usePageContext();

    const selectedPerformance = performances?.find(p => p.id === selectedPerformanceId);
    const isSingleDateView = hasSinglePerformance || selectedPerformance;

    const handlePrevious = () => {
      if (!performances) return;

      const currentIndex = performances.findIndex(p => p.id === selectedPerformanceId);

      if (currentIndex > 0) {
        const previousPerformance = performances[currentIndex - 1];
        setSelectedPerformanceId(previousPerformance.id);
      }
    };

    const handleNext = () => {
      if (!production?.performances) return;

      if (!selectedPerformanceId) {
        // Select Upcoming performance
        const upcomingPerformance = getEntityLatestUpcomingPerformance(production, '', ENTITY_TYPES.PRODUCTION);

        if (upcomingPerformance?.id) {
          setSelectedPerformanceId(upcomingPerformance.id);
          return;
        }
      }

      const currentIndex = performances.findIndex(p => p.id === selectedPerformanceId);

      if (currentIndex < performances.length - 1) {
        const nextPerformance = performances[currentIndex + 1];
        setSelectedPerformanceId(nextPerformance.id);
      }
    };

    if (entityType === ENTITY_TYPES.PRODUCTION) {
      return (
        <div ref={ref}>
          <SingleDate
            production={production}
            selectedPerformance={selectedPerformance}
            isProdStubPage
            styles={styles}
          />
        </div>
      );
    }

    return (
      <div ref={ref}>
        {!isStickyHeader && <SectionHeader title={t(`${TP}.FN_DATES_VENUES`)} />}

        <div className={classnames(classes.content, { [classes.stickyHeader]: isStickyHeader })}>
          {!hasSinglePerformance && (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div className={classes.nextPrevIconContainer} onClick={handlePrevious}>
              <SpriteIcon icon="chevron_left" size={16} />
              <Typography variant="p" color="secondary" size={14}>
                {t(`${TP}.FN_PREV`)}
              </Typography>
            </div>
          )}
          {isSingleDateView ? (
            <SingleDate
              production={production}
              selectedPerformance={selectedPerformance}
              isStickyHeader={isStickyHeader}
            />
          ) : (
            <MultipleDates production={production} t={t} isStickyHeader={isStickyHeader} />
          )}
          {!hasSinglePerformance && (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div className={classes.nextPrevIconContainer} onClick={handleNext}>
              <Typography variant="p" color="secondary" size={14}>
                {t(`${TP}.m_NEXTSTEP`)}
              </Typography>
              <SpriteIcon icon="chevron_right" size={16} />
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default DateAndVenue;
